import axios from 'axios';
import Sentiment from 'sentiment';

const sentiment = new Sentiment();
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

console.log('YouTube API Key loaded:', YOUTUBE_API_KEY ? `Yes (starts with ${YOUTUBE_API_KEY.substring(0, 4)}...)` : 'No');

// Check if a comment is relevant to the searched product
const isRelevant = (text, brand, model) => {
  const normalized = text.toLowerCase();
  const brandLower = brand.toLowerCase();
  const modelTerms = model.toLowerCase().split(' ').filter(t => t.length > 1);

  // Skip very short comments — likely not useful
  if (normalized.trim().length < 15) return false;

  // Skip generic comments
  const genericPhrases = [
    'great video', 'nice video', 'good video', 'love this video',
    'awesome video', 'thanks for', 'thank you', 'subscribed',
    'first', 'early', 'who is watching', 'anybody watching',
    'lol', 'haha', 'omg', '😂', '❤️', '🔥'
  ];
  if (genericPhrases.some(phrase => normalized.includes(phrase) && normalized.length < 30)) return false;

  // Must mention brand OR at least one model term
  const hasBrand = normalized.includes(brandLower);
  const hasModelTerm = modelTerms.some(term => normalized.includes(term));

  return hasBrand || hasModelTerm;
};

export const searchYouTubeVideos = async (query, maxResults = 5) => {
  try {
    const response = await axios.get('https://www.googleapis.com/youtube/v3/search', {
      params: {
        part: 'snippet',
        q: `${query} review`,
        maxResults: maxResults,
        type: 'video',
        key: YOUTUBE_API_KEY,
        order: 'relevance'
      }
    });

    return response.data.items || [];
  } catch (error) {
    console.error('Error searching YouTube videos:', error);
    return [];
  }
};

export const getVideoComments = async (videoId, maxComments = 50) => {
  try {
    const response = await axios.get('https://www.googleapis.com/youtube/v3/commentThreads', {
      params: {
        part: 'snippet',
        videoId: videoId,
        maxResults: Math.min(maxComments, 100),
        key: YOUTUBE_API_KEY,
        order: 'relevance'
      }
    });

    return response.data.items || [];
  } catch (error) {
    console.error(`Error fetching comments for video ${videoId}:`, error);
    return [];
  }
};

export const analyzeYouTubeSentiment = (comments, brand, model) => {
  if (!comments || comments.length === 0) {
    return {
      overall: 0,
      overall_percentage: 50,
      positive: 0,
      neutral: 0,
      negative: 0,
      top_mentions: [],
      recent_trend: 'stable',
      total_comments_analyzed: 0
    };
  }

  let totalScore = 0;
  let positiveCount = 0;
  let neutralCount = 0;
  let negativeCount = 0;
  const scoredComments = [];

  comments.forEach(comment => {
    const text = comment.snippet.topLevelComment.snippet.textDisplay;
    const likes = comment.snippet.topLevelComment.snippet.likeCount || 0;

    const result = sentiment.analyze(text);
    const score = result.comparative;

    totalScore += score;

    if (score > 0.05) positiveCount++;
    else if (score < -0.05) negativeCount++;
    else neutralCount++;

    let sentimentLabel = 'neutral';
    if (score > 0.1) sentimentLabel = 'positive';
    else if (score < -0.1) sentimentLabel = 'negative';

    // Only include relevant comments in top mentions
    const relevant = isRelevant(text, brand, model);

    scoredComments.push({
      platform: 'YouTube',
      text: text.length > 200 ? text.substring(0, 200) + '...' : text,
      sentiment: sentimentLabel,
      sentimentScore: score,
      likes,
      author: comment.snippet.topLevelComment.snippet.authorDisplayName,
      videoId: comment.snippet.videoId,
      relevant
    });
  });

  const total = comments.length;
  const overallScore = total > 0 ? totalScore / total : 0;
  const overallPercentage = Math.min(100, Math.max(0, Math.round(((overallScore + 1) / 2) * 100)));

  // Prefer relevant comments, ranked by likes then sentiment strength
  const relevantComments = scoredComments.filter(c => c.relevant);
  const rankingPool = relevantComments.length >= 3 ? relevantComments : scoredComments;

  rankingPool.sort((a, b) => {
    if (b.likes !== a.likes) return b.likes - a.likes;
    return Math.abs(b.sentimentScore) - Math.abs(a.sentimentScore);
  });

  return {
    overall: overallScore,
    overall_percentage: overallPercentage,
    positive: Math.round((positiveCount / total) * 100),
    neutral: Math.round((neutralCount / total) * 100),
    negative: Math.round((negativeCount / total) * 100),
    top_mentions: rankingPool.slice(0, 3).map(c => ({
      platform: c.platform,
      text: c.text,
      sentiment: c.sentiment,
      likes: c.likes,
      author: c.author,
      videoId: c.videoId
    })),
    recent_trend: 'stable',
    total_comments_analyzed: total
  };
};

export const getYouTubeSentiment = async (brand, model) => {
  try {
    const searchQuery = `${brand} ${model}`;
    console.log(`🎬 Searching YouTube for: ${searchQuery} reviews...`);

    const videos = await searchYouTubeVideos(searchQuery, 5);

    if (videos.length === 0) {
      console.log('No YouTube videos found');
      return null;
    }

    console.log(`Found ${videos.length} videos, fetching comments...`);

    let allComments = [];
    for (const video of videos.slice(0, 3)) {
      const comments = await getVideoComments(video.id.videoId, 30);
      allComments = [...allComments, ...comments];
      console.log(`- ${video.snippet.title}: ${comments.length} comments`);
    }

    console.log(`Total comments collected: ${allComments.length}`);

    if (allComments.length === 0) {
      return null;
    }

    const sentimentData = analyzeYouTubeSentiment(allComments, brand, model);
    return sentimentData;
  } catch (error) {
    console.error('Error getting YouTube sentiment:', error);
    return null;
  }
};